import React from 'react'

import LockIcon from '@mui/icons-material/Lock'
import { Button, InputAdornment, Typography, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { BusinessTopic, TopicType } from 'constants/enums/TopicTypeEnum'

import BusinessTopicChips from 'components/BusinessTopicChips'
import { BaseTextField } from 'components/Form'
import WarningMessage from 'components/WarningMessage/WarningMessage'

import { typography } from 'theme/typography'

import { commonStyles } from 'styles/common.styles'

import { styles } from './Section1.styles'

export const TEST_ID = 'dress-up-section1'

export type Props = {
	companyProblem: string
	createPrompt: () => void
	editable: boolean
	generateScenarioDisabled: () => boolean
	innovationCompany: string
	innovationTopic: TopicType
	isLoading: boolean
	resetAllField: () => void
	setCompanyProblem: (val: string) => void
	setInnovationCompany: (val: string) => void
	setInnovationTopic: (val: TopicType) => void
}

const Section1 = ({
	innovationCompany,
	setInnovationCompany,
	innovationTopic,
	setInnovationTopic,
	companyProblem,
	setCompanyProblem,
	generateScenarioDisabled,
	resetAllField,
	isLoading,
	createPrompt,
	editable,
}: Props) => {
	const theme = useTheme()

	React.useEffect(() => {
		const storedCompany = localStorage.getItem('innovationCompany')
		const storedTopic = localStorage.getItem('innovationTopic')
		const storedProblem = localStorage.getItem('companyProblem')
		if (storedCompany) setInnovationCompany(storedCompany)
		if (storedTopic) setInnovationTopic(storedTopic as TopicType)
		if (storedProblem) setCompanyProblem(storedProblem)
	}, [setInnovationCompany, setInnovationTopic, setCompanyProblem])
	React.useEffect(() => {
		const debounce = setTimeout(() => {
			localStorage.setItem('innovationCompany', innovationCompany)
			localStorage.setItem('innovationTopic', innovationTopic)
			localStorage.setItem('companyProblem', companyProblem)
		}, 200)
		return () => clearTimeout(debounce)
	}, [innovationCompany, innovationTopic, companyProblem])

	return (
		<>
			<Typography
				variant={'overline'}
				sx={{ ...commonStyles.header, ...styles.innovationHeader }}
			>
				1. INNOVATION TOPIC
			</Typography>
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display={'flex'}
					flexDirection={'column'}
					rowGap={'3rem'}
					flexGrow="1"
				>
					<Typography
						sx={commonStyles.header}
						data-testid={`${TEST_ID}__company-brainstorm-prompt`}
					>
						What company are you brainstorming about today?{' '}
						<Box component="span" sx={typography.asterisk}>
							*
						</Box>
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
							required: false,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-name-input`}
						label="Enter company name here."
						value={innovationCompany}
						onChangeValue={(val) => setInnovationCompany(val)}
						required
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiInputBase-input': {
								boxSizing: 'content-box',
								height: '100%',
							},
							...styles.textField,
						}}
					/>
					<Typography
						sx={commonStyles.header}
						data-testid={`${TEST_ID}__business-topic-prompt`}
					>
						What business topic do you want to focus on?{' '}
						<Box component="span" sx={typography.asterisk}>
							*
						</Box>
					</Typography>
					<BusinessTopicChips
						innovationTopic={innovationTopic}
						setInnovationTopic={setInnovationTopic}
						backgroundColor={
							theme.palette.chipColor.dressup.main as string
						}
						editable={editable}
						filterOut={BusinessTopic.Operations}
					/>
					<Typography
						data-testid={`${TEST_ID}__summarize-prompt`}
						sx={{ ...commonStyles.header, ...styles.header }}
					>
						Summarize the business problem you want to ideate on (in
						5-10 words).{' '}
						<Box component="span" sx={typography.asterisk}>
							*
						</Box>
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-problem-input`}
						label="Summarize in 5-10 words"
						onChangeValue={(val) => setCompanyProblem(val)}
						value={companyProblem}
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiInputBase-input': {
								boxSizing: 'content-box',
								height: '100%',
							},
							...styles.textField,
						}}
					/>
					<WarningMessage prompt={companyProblem} />
					<Box
						display="flex"
						sx={{
							flexDirection: {
								sm: 'row',
							},
						}}
						gap="2rem"
					>
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__create-prompt-button`}
							disabled={generateScenarioDisabled() || isLoading}
							onClick={createPrompt}
						>
							START IDEATING
						</Button>
						<Button
							disabled={!editable}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__reset-all-button`}
							onClick={resetAllField}
						>
							RESET ALL FIELDS
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	)
}
export default Section1
